/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import minBy from 'utils/minBy';
import useKeys from 'effects/keys';
import useInview from 'effects/inview';
import scrollTo from 'utils/scrollTo';
import Poster from './Poster/Poster';
import './Video.scss';

/**
 * This is the Video component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
const Video = forwardRef(
  (
    {
      src: propSrc,
      srcSet,
      textTracks,
      width,
      height,
      autoPlay,
      scrollTo: scrollToOptions,
      onEnded,
      ...props
    },
    parentRef
  ) => {
    const player = useRef(null);
    const posterRef = useRef(null);
    const [index, setIndex] = useState(null);
    const [poster, setPoster] = useState(true);
    const [focus, setFocus] = useState(false);
    const [ref, inview] = useInview({ threshold: 0.5 });

    useKeys('video', !poster, {
      esc: () => {
        if (player.current) {
          player.current.pause();
          player.current.currentTime = 0;
          setPoster(true);
          setFocus(true);
        }
      },
    });

    useEffect(() => {
      function onResize() {
        if (srcSet.length) {
          const orientation =
            window.innerWidth < window.innerHeight ? 'portrait' : 'landscape';

          let index = srcSet.findIndex(
            source =>
              source.width <= window.innerWidth &&
              (source.orientation === orientation ||
                source.orientation !== 'portrait')
          );

          if (index < 0) {
            const min = minBy(
              srcSet.filter(({ width }) => !!width),
              ({ width }) => width
            );

            if (min) {
              index = srcSet.findIndex(({ width }) => width === min.width);
            }
          }

          setIndex(index);
        }
      }

      window.addEventListener('resize', onResize);
      onResize();

      return function cleanup() {
        window.removeEventListener('resize', onResize);
      };
    }, [srcSet]);

    useEffect(() => {
      if (!inview) {
        if (player.current) {
          player.current.pause();
          player.current.currentTime = 0;
          setPoster(true);
        }
      }
    }, [inview]);

    useEffect(() => {
      if (focus) {
        posterRef.current.focus();
        setFocus(false);
      }
    }, [focus]);

    useImperativeHandle(parentRef, () => ({
      play: () => {
        if (player.current) {
          player.current.play();
        }
      },
      pause: () => {
        player.current.pause();
      },
    }));

    function getSource() {
      if (index === null || index < 0) {
        if (!propSrc) return null;
        return propSrc;
      }

      return srcSet[index].link;
    }

    function getTracks() {
      if (!textTracks) return null;

      return textTracks.map(({ srcLang, ...props }) => (
        <track key={srcLang} srcLang={srcLang} {...props} />
      ));
    }

    return (
      <div ref={ref} className="video" style={{ '--ratio': height / width }}>
        <div>
          <video
            ref={player}
            src={getSource()}
            width={width}
            height={height}
            controls={!poster}
            autoPlay={autoPlay}
            onPlay={() => {
              setPoster(false);
              scrollTo(ref.current, scrollToOptions);
            }}
            onEnded={() => {
              player.current.currentTime = 0;
              setPoster(true);
              setFocus(true);
              onEnded();
            }}
          >
            {getTracks()}
          </video>
          <CSSTransition in={poster} timeout={1000} unmountOnExit={true}>
            <Poster
              ref={posterRef}
              {...props}
              onPlay={() => {
                player.current.play();
              }}
            />
          </CSSTransition>
        </div>
      </div>
    );
  }
);

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Video.propTypes = {
  src: PropTypes.string,
  srcSet: PropTypes.array,
  textTracks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  autoPlay: PropTypes.bool,
  scrollTo: PropTypes.object.isRequired,
  onEnded: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Video.defaultProps = {
  src: null,
  srcSet: [],
  textTracks: [],
  width: 1600,
  height: 900,
  autoPlay: false,
  scrollTo: {
    alignTop: true,
    offset: 64,
  },
  onEnded: () => {},
};

export default Video;
